import { useMemo, useState } from 'react'
import { Button } from '../Button'
import { Search } from '../Search'
import { formatUTCDate, RatingChip, VsOverall } from './shared'

export interface DealHistoryCycle {
	cycleId: string
	dealName: string | null
	cycleName: string | null
	roleName: string | null
	closeDate: string | Date | null
	avgRating: number | null
	vsOverall: number | null
	completedEvaluations: number
}

export interface DealHistoryProfile {
	cycles: DealHistoryCycle[]
}

export function DealHistoryTab({
	profile,
	onViewReport,
	scaleMax = 10,
}: Readonly<{
	profile: DealHistoryProfile
	onViewReport: (cycleId: string) => void
	scaleMax?: number
}>) {
	const [searchInput, setSearchInput] = useState('')

	const filtered = useMemo(() => {
		const q = searchInput.trim().toLowerCase()
		if (!q) return profile.cycles
		return profile.cycles.filter((c) => {
			const haystack = [c.dealName, c.cycleName, c.roleName].filter(Boolean).join(' ').toLowerCase()
			return haystack.includes(q)
		})
	}, [profile.cycles, searchInput])

	return (
		<div className='flex flex-col gap-3'>
			<Search value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder='Search deals' />

			<div className='overflow-x-auto rounded-lg border border-neutral-200'>
				<table className='w-full text-sm'>
					<thead className='bg-neutral-50'>
						<tr>
							<th className='px-4 py-3 text-left font-medium text-neutral-600'>Deal Name</th>
							<th className='px-4 py-3 text-left font-medium text-neutral-600'>Role</th>
							<th className='px-4 py-3 text-left font-medium text-neutral-600'>Close Date</th>
							<th className='px-4 py-3 text-left font-medium text-neutral-600'>Rating</th>
							<th className='px-4 py-3 text-left font-medium text-neutral-600'>vs. Overall</th>
							<th className='px-4 py-3 text-left font-medium text-neutral-600'>Action</th>
						</tr>
					</thead>
					<tbody className='divide-y divide-neutral-200'>
						{profile.cycles.length === 0 && (
							<tr>
								<td colSpan={6} className='px-4 py-8 text-center text-neutral-500'>
									No deal team participation found for this participant.
								</td>
							</tr>
						)}
						{profile.cycles.length > 0 && filtered.length === 0 && (
							<tr>
								<td colSpan={6} className='px-4 py-8 text-center text-neutral-500'>
									No deals match the current search.
								</td>
							</tr>
						)}
						{filtered.map((cycle) => (
							<tr key={cycle.cycleId} className='hover:bg-neutral-50'>
								<td className='px-4 py-3 font-medium text-neutral-900'>{cycle.dealName || cycle.cycleName}</td>
								<td className='px-4 py-3 text-neutral-700'>{cycle.roleName ?? '—'}</td>
								<td className='px-4 py-3 text-neutral-500'>{formatUTCDate(cycle.closeDate)}</td>
								<td className='px-4 py-3'>
									<RatingChip value={cycle.avgRating} scaleMax={scaleMax} className='py-1' />
								</td>
								<td className='px-4 py-3'>
									<VsOverall value={cycle.vsOverall} />
								</td>
								<td className='px-4 py-3'>
									<Button
										variant='secondary'
										size='small'
										disabled={cycle.completedEvaluations === 0}
										onClick={() => onViewReport(cycle.cycleId)}>
										Report
									</Button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	)
}
